import forge from "node-forge";
import { PUBLIC_KEY, PRIVATE_KEY } from "./rsa";

export default class Req<T> {
  static _publicKey: forge.pki.rsa.PublicKey;
  static _privateKey: forge.pki.rsa.PrivateKey;

  /**
   * Decrypt value.
   * TODO: Validate
   */
  static decrypt(value: string): string {
    if (typeof value === "string") {
      const text = Req.privateKey.decrypt(value, "RSA-OAEP", {
        md: forge.md.sha256.create(),
        mgf1: {
          md: forge.md.sha1.create(),
        },
      });
      return text;
    }
    return value;
  }

  /**
   * Decrypt object.
   */
  static decryptObject(object: object): object {
    const decObject = JSON.parse(JSON.stringify(object));
    const recursiveDec = (obj) => {
      for (let prop in obj) {
        if (obj[prop] instanceof Object) {
          recursiveDec(obj[prop]);
        } else {
          obj[prop] = Req.decrypt(obj[prop]);
        }
      }
    };
    recursiveDec(decObject);
    return decObject;
  }

  /**
   * Encrypt value.
   */
  static encrypt(value: string): string {
    if (typeof value === "string") {
      const cypher = Req.publicKey.encrypt(
        forge.util.encodeUtf8(value),
        "RSA-OAEP",
        {
          md: forge.md.sha256.create(),
          mgf1: {
            md: forge.md.sha1.create(),
          },
        }
      );
      return forge.util.encode64(cypher);
    }
    return value;
  }

  /**
   * Encrypt object.
   */
  static encryptObject(object: object): object {
    const encObject = JSON.parse(JSON.stringify(object));
    const recursiveEnc = (obj: any) => {
      for (let prop in obj) {
        if (obj[prop] instanceof Object) {
          recursiveEnc(obj[prop]);
        } else {
          obj[prop] = Req.encrypt(obj[prop]);
        }
      }
    };

    recursiveEnc(encObject);
    return encObject;
  }

  /**
   * Make a get request.
   */
  static get(url: string) {
    return Req.send(url, "GET");
  }

  /**
   * Get CSRF values that comes into meta tags in header document.
   */
  static getCsrfValues(): object | undefined {
    const metaName = document.querySelector("meta[name=_csrf_header]");
    const metaValue = document.querySelector("meta[name=_csrf]");
    if (metaName && metaValue) {
      return { name: metaName["content"], value: metaValue["content"] };
    }
    return undefined;
  }

  /**
   * Make a post request.
   */
  static post(url: string, data?: object) {
    return Req.send(url, "POST", data);
  }

  /**
   * Make a request.
   */
  static send(url: string, method: string, data = {}) {
    const csrf = Req.getCsrfValues();
    const init = {
      method: method,
      headers: { "Content-Type": "application/json" },
    };

    if (csrf) {
      init[csrf["name"]] = csrf["value"];
    }

    if (method === "POST") {
      const encData = Req.encryptObject(data || {});
      init["body"] = JSON.stringify(encData);
    }

    return new Promise(async (res, rej) => {
      try { 
        const resp = await fetch(url, init);
        if (!resp.ok) {
          throw new Error(`${resp.status} - ${resp.statusText}`);
        }
        const json = await resp.json();
        res(json);
      } catch (err: any) {
        console.log(`Req: [${method}: ${url}] ${err.message}`);
        res({ success: false, message: err.message });
      }
    });
  }

  /**
   * Get enc property.
   */
  static get publicKey(): forge.pki.rsa.PublicKey {
    if (!Req._publicKey) {
      Req._publicKey = forge.pki.publicKeyFromPem(PUBLIC_KEY);
    }

    return Req._publicKey;
  }

  /**
   * Get dec property.
   */
  static get privateKey(): forge.pki.rsa.PrivateKey {
    if (!Req._privateKey) {
      Req._privateKey = forge.pki.privateKeyFromPem(PRIVATE_KEY);
    }

    return Req._privateKey;
  }
}
